// Match Lineup — pure layout/data helpers (TP_BUILD_BRIEF_MATCHES_STATS_LINEUP
// Part B; design handoff "Match Lineup Component.dc.html" is the spec).
//
// Everything here is deterministic and unit-testable: the provider coordinate
// normalisation, the formation-band layout (one band per formation line,
// keeper at the bottom, attack at the top, each player owning a full column),
// the extra-time minute notation ("45+2'") and the XI/bench split.
//
// No React in this file — the SVG symbol set lives in incidentSymbols.js.

// ── coordinate normalisation (handoff index board, verbatim) ───────────────
//
//   home → top = (100 − y)%, left = x%
//   away → top = y%,         left = (100 − x)%
//
// Both teams render as a vertical half-pitch with the keeper at the bottom
// and the attack at the top.
export const normalizeCoordinates = (side, x, y) => {
  if (x === null || x === undefined || x === "" || y === null || y === undefined || y === "") {
    return null;
  }
  const nx = Number(x);
  const ny = Number(y);
  if (!Number.isFinite(nx) || !Number.isFinite(ny)) return null;
  // (0,0) is the provider's "no placement data" sentinel on low-coverage
  // matches (whole XI at the corner, position/shirt_number empty too) —
  // treat it as missing, never as a real corner placement.
  if (nx === 0 && ny === 0) return null;
  if (side === "away") return { top: ny, left: 100 - nx };
  return { top: 100 - ny, left: nx };
};

// ── formation parsing ──────────────────────────────────────────────────────

// "4-2-3-1" -> [4, 2, 3, 1]. Anything unparseable -> [].
export const parseFormation = (formation) =>
  String(formation || "")
    .split("-")
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);

// Column width for a band of n players (handoff columnWidth, verbatim):
// columns must track the spacing (100/(n+1)), not 100/n, or neighbours
// overlap. Returned as a CSS percentage string.
export const columnWidth = (n) =>
  Math.max(16, Math.round((100 / (n + 1)) * 10) / 10 - 1.5) + "%";

// ── XI / bench split ───────────────────────────────────────────────────────

// Provider lineup arrays mix starters and substitutes; `first` marks the
// starting eleven (1/"1"/true). Everyone else is bench.
export const splitLineup = (players) => {
  const xi = [];
  const bench = [];
  (Array.isArray(players) ? players : []).forEach((p) => {
    if (!p) return;
    if (Number(p.first) === 1 || p.first === true) xi.push(p);
    else bench.push(p);
  });
  return { xi, bench };
};

// ── starter ordering for band assignment ───────────────────────────────────

// The formation bands are filled keeper-first, then line by line towards the
// attack, left to right inside a line — which is exactly the order the
// provider delivers the starting eleven in (the handoff consumes it verbatim
// and so do we; re-deriving the order from the jittery per-player x/y breaks
// intra-line ordering). One safety only: a goalkeeper not sitting at index 0
// is moved to the front so the keeper band never shows an outfielder.
export const orderStarters = (xi) => {
  const list = (xi || []).slice();
  const gkIdx = list.findIndex((p) => p && String(p.position) === "G");
  if (gkIdx > 0) {
    const [gk] = list.splice(gkIdx, 1);
    list.unshift(gk);
  }
  return list;
};

// ── formation-band layout (handoff formationLayout, verbatim maths) ────────
//
// Positions come from the formation, not hand-placed coordinates: one band
// per line ([1, ...formation parts], keeper band first), bands spread evenly
// over the pitch (y from 12 to 90 in handoff space, rendered as
// top = 100 − y so the keeper sits at the bottom), players spread evenly
// across the band ((i+1)/(n+1)), each owning a full column of the pitch.
//
// Starters beyond the formation sum fall back to their normalised provider
// coordinates (never dropped); a formation slot without a player renders
// nothing. Without any parseable formation string every starter falls back
// to normalised provider coordinates.
// A pitch is only worth rendering when SOMETHING can be placed: a parseable
// formation string, or at least one starter with real provider coordinates.
// Low-coverage matches (confirmed name-only lineups) have neither — the
// caller falls back to a list presentation of the XI instead of an empty
// pitch (CEO finding C1/C4, Liestal case).
export const hasPlacementData = (xi, formation, side) =>
  parseFormation(formation).length > 0 ||
  (xi || []).some((p) => normalizeCoordinates(side, p.x, p.y) !== null);

export const layoutTeam = (xi, formation, side) => {
  const players = orderStarters(xi);
  const parts = parseFormation(formation);
  const out = [];
  let idx = 0;
  if (parts.length > 0) {
    const rows = [1].concat(parts);
    const R = rows.length;
    rows.forEach((n, ri) => {
      const y = 12 + ri * (78 / (R - 1));
      for (let i = 0; i < n; i += 1) {
        const p = players[idx];
        idx += 1;
        if (!p) return;
        out.push({
          player: p,
          left: Math.round(((i + 1) / (n + 1)) * 1000) / 10,
          top: Math.round((100 - y) * 10) / 10,
          columnWidth: columnWidth(n),
        });
      }
    });
  }
  // fallback: remaining (or all, when no formation) via provider coordinates
  while (idx < players.length) {
    const p = players[idx];
    idx += 1;
    const c = normalizeCoordinates(side, p.x, p.y);
    out.push({
      player: p,
      left: c ? c.left : 50,
      top: c ? c.top : 50,
      columnWidth: columnWidth(4),
    });
  }
  return out;
};

// ── minute notation ────────────────────────────────────────────────────────

// 62 -> "62'", {minute: 45, addtime: 2} -> "45+2'". No minute -> null
// (renders nothing — never a dash).
export const formatMinute = (minute, addtime) => {
  const m = Number(minute);
  if (!Number.isFinite(m) || m <= 0) return null;
  const a = Number(addtime);
  return a > 0 ? `${m}+${a}'` : `${m}'`;
};

// ── incident person references ─────────────────────────────────────────────
//
// LIVE-SHAPE CORRECTION (QA 2026-08-17, real staging ingest): the provider's
// incident person fields (player, in_player, out_player, assist1, assist2)
// arrive as OBJECTS `{id, name}` in the persisted rows — the §0-audit note
// ("= Name") described a plain-string variant. Both shapes exist, so every
// comparison goes through these helpers: id-first (stable), name fallback.
export const personName = (ref) => {
  if (ref === null || ref === undefined || ref === "") return null;
  if (typeof ref === "object") {
    return ref.name === undefined || ref.name === "" ? null : ref.name;
  }
  return ref;
};

export const personId = (ref) =>
  ref && typeof ref === "object" && ref.id !== undefined && ref.id !== ""
    ? ref.id
    : null;

// True when an incident person reference points at this lineup player.
export const isSamePerson = (ref, player) => {
  if (!ref || !player) return false;
  const id = personId(ref);
  if (id !== null && player.id !== null && player.id !== undefined) {
    return String(id) === String(player.id);
  }
  const name = personName(ref);
  return name !== null && name === player.name;
};

// ── substitutions ──────────────────────────────────────────────────────────

// Collect the substitution pairs of one team from its players' incidents
// (type 9), de-duplicated (the provider repeats the incident on both players
// involved). Rows: { minute, addtime, label, outName, inName, outRef, inRef }
// — the *Ref fields keep the raw provider reference (string OR {id, name})
// so the container can resolve the actual player objects id-first.
export const collectSubstitutions = (players) => {
  const rows = [];
  const seen = {};
  (players || []).forEach((p) => {
    (p.incidents || []).forEach((inc) => {
      if (Number(inc?.type) !== 9) return;
      const outRef = inc.out_player || null;
      const inRef = inc.in_player || null;
      const outName = personName(outRef) || "";
      const inName = personName(inRef) || "";
      const key = `${inc.minute}|${inc.addtime}|${personId(outRef) || outName}|${
        personId(inRef) || inName
      }`;
      if (seen[key]) return;
      seen[key] = true;
      rows.push({
        minute: inc.minute,
        addtime: inc.addtime,
        label: formatMinute(inc.minute, inc.addtime),
        outName,
        inName,
        outRef,
        inRef,
      });
    });
  });
  return rows;
};

// The minute a bench player came on (his own type-9 incident naming him as
// in_player, or — some feeds — any type-9 in his list). Null when he stayed
// on the bench.
export const cameOnMinute = (player) => {
  const incs = (player && player.incidents) || [];
  const hit =
    incs.find(
      (i) => Number(i?.type) === 9 && isSamePerson(i.in_player, player)
    ) || incs.find((i) => Number(i?.type) === 9 && !i.out_player);
  return hit ? formatMinute(hit.minute, hit.addtime) : null;
};

// ── incident glyph grouping (handoff incidentIcons, data half) ─────────────

// Groups repeat glyphs of the same type preserving first-seen order:
// ['goal','goal','yellow'] -> [{type:'goal', count:2}, {type:'yellow', count:1}]
export const groupGlyphKeys = (keys) => {
  const order = [];
  const byType = {};
  (keys || []).forEach((k) => {
    if (!k) return;
    if (!byType[k]) {
      byType[k] = 0;
      order.push(k);
    }
    byType[k] += 1;
  });
  return order.map((t) => ({ type: t, count: byType[t] }));
};

// ── responsive ladder (handoff buildBoard; Laptop-Band angepasst) ──────────

// All keyed on the COMPONENT width (the pane is narrower than the viewport).
//
// Laptop-Band-Fix (Marin 2026-08-29, 15-Zoll-Repro seines Vaters): Im
// Handoff-Ladder stellte 1024–1439 die TEAMS nebeneinander und stapelte
// dafür Bank unter das Spielfeld — auf normalen Laptops (effektiv
// 1024–1439px Panelbreite durch OS-Skalierung) wirkte das als „riesiges
// Spielfeld, Aufstellung erst darunter". Das Band stellt jetzt umgekehrt
// die Teams UNTEREINANDER und dafür pro Team Spielfeld+Bank NEBENEINANDER
// (Pitch ≈58% von voller Panelbreite). ≥1440 und echtes Mobile (<760)
// bleiben exakt wie im Handoff.
//
// Nachschärfung (Staging-Vermessung 2026-08-29, CEO): Die realen Panel-
// breiten liegen NIEDRIGER als angenommen — Spielerprofil@1366-Viewport
// = 822px, Club@1366 = 856px. Die 900er-Schwelle verfehlte damit genau
// den 15-Zoll-Repro-Fall. Row-Band beginnt darum bei 760 (Pitch dann
// 440–520px); im schmalen Row-Band 760–1023 gelten die Mobile-Knoten
// (34px, 11px-Namen), damit 38er-Nodes das schmale Spielfeld nicht
// überladen — 390-Board-Referenz: 32px-Nodes auf 390px Breite.
export const boardMetrics = (w) => ({
  nodeSize:
    w <= 390 ? 32 : w < 1024 ? 34 : w < 1440 ? 38 : w < 1920 ? 42 : w < 2560 ? 46 : 52,
  nameSize: w < 1024 ? 11 : w < 1920 ? 12 : 13,
  cardPad: w <= 390 ? 14 : w < 1440 ? 18 : 24,
  h1: w <= 390 ? 16 : 18,
  h2: w <= 390 ? 14 : 15,
  teamsDir: w < 1440 ? "column" : "row",
  teamsGap: w < 900 ? 28 : w < 1440 ? 24 : 32,
  innerDir: w >= 760 ? "row" : "column",
  pitchFlex: w >= 760 ? "1 1 58%" : "1 1 auto",
  benchFlex: w >= 760 ? "1 1 42%" : "1 1 auto",
  incidentSize: w <= 390 ? 11 : 13,
});
